import { AdminProfile, NavPage } from '../types';

interface Props {
  activePage: NavPage;
  onNavigate: (page: NavPage) => void;
  onLogout: () => void;
  adminProfile: AdminProfile | null;
}

const NAV_MAIN: { id: NavPage; label: string; icon: string }[] = [
  { id: 'dashboard',    label: 'Dashboard',    icon: '▦' },
  { id: 'athletes',     label: 'Athletes',     icon: '◈' },
  { id: 'institutions', label: 'Institutions', icon: '⬡' },
  { id: 'mentors',      label: 'Mentors',      icon: '◎' },
  { id: 'huddle',       label: 'The Huddle',   icon: '◉' },
];

const NAV_SECONDARY: { id: NavPage; label: string; icon: string }[] = [
  { id: 'waitlist', label: 'Waitlist',  icon: '▤' },
  { id: 'insights', label: 'Insights', icon: '⟿' },
  { id: 'reports',  label: 'Reports',  icon: '⬇' },
];

export default function Sidebar({ activePage, onNavigate, onLogout, adminProfile }: Props) {
  const isBthdInternal = !adminProfile?.institutionType || adminProfile.institutionType === 'bthd_internal';

  const STUDENT_PORTAL_TYPES = ['high_school', 'prep_school', 'aau'];
  const isStudentPortal = STUDENT_PORTAL_TYPES.includes(adminProfile?.institutionType ?? '');
  const ORG_ADMIN_PAGES: NavPage[] = isStudentPortal ? ['dashboard'] : ['dashboard', 'athletes', 'insights'];

  const visibleMain = isBthdInternal
    ? NAV_MAIN
    : NAV_MAIN.filter(item => ORG_ADMIN_PAGES.includes(item.id));

  const visibleSecondary = isBthdInternal
    ? NAV_SECONDARY
    : NAV_SECONDARY.filter(item => ORG_ADMIN_PAGES.includes(item.id));

  const NavItem = ({ item }: { item: { id: NavPage; label: string; icon: string } }) => {
    const isActive = activePage === item.id;
    return (
      <div
        onClick={() => onNavigate(item.id)}
        style={{
          display: 'flex', alignItems: 'center', gap: 9,
          padding: '9px 10px', borderRadius: 8, cursor: 'pointer', marginBottom: 2,
          background: isActive ? '#bf1e2e' : 'transparent',
          color: isActive ? '#fff' : '#8da0bc',
          fontWeight: isActive ? 600 : 400,
          transition: 'all 0.15s', fontSize: 13,
        }}
        onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
        onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
      >
        <span style={{ fontSize: 15, width: 18, textAlign: 'center', opacity: 0.9 }}>{item.icon}</span>
        <span style={{ flex: 1 }}>{item.label}</span>
      </div>
    );
  };

  return (
    <aside style={{
      width: 'var(--sidebar-w)',
      background: 'var(--surface)',
      borderRight: '1px solid var(--border)',
      display: 'flex', flexDirection: 'column',
      position: 'fixed', top: 0, left: 0,
      height: '100vh', zIndex: 100, overflowY: 'auto',
    }}>
      {/* Logo */}
      <div style={{ padding: '22px 18px 18px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 50, height: 50, borderRadius: '50%',
            backgroundColor: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', flexShrink: 0,
          }}>
            <img src="/logo.png" alt="BTHD" style={{ width: '300%', height: '300%', objectFit: 'contain' }} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: '#bf1e2e', fontWeight: 700, letterSpacing: 1.5, marginTop: 2, textTransform: 'uppercase' }}>Admin Hub</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ padding: '12px 10px', flex: 1 }}>
        <div className="label-caps" style={{ padding: '0 8px', marginBottom: 6 }}>Main</div>
        {visibleMain.map(item => <NavItem key={item.id} item={item} />)}
        {visibleSecondary.length > 0 && (
          <>
            <div className="label-caps" style={{ padding: '12px 8px 6px' }}>Analytics</div>
            {visibleSecondary.map(item => <NavItem key={item.id} item={item} />)}
          </>
        )}
      </nav>

      {/* Logout & User */}
      <div style={{ padding: '12px 10px', borderTop: '1px solid var(--border)' }}>
        <button
          onClick={onLogout}
          style={{
            width: '100%', padding: '9px 10px', marginBottom: 12,
            borderRadius: 8, border: '1px solid #bf1e2e',
            background: 'transparent', color: '#bf1e2e',
            fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'all 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#bf1e2e'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#bf1e2e'; }}
        >
          LOGOUT
        </button>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 9,
          background: 'var(--surface-2)', borderRadius: 9, padding: '10px',
        }}>
          {(() => {
            const isBthdInternal = !adminProfile?.institutionType || adminProfile.institutionType === 'bthd_internal';
            const name = adminProfile?.fullName || 'Admin';
            const initials = name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
            const sublabel = isBthdInternal
              ? 'BTHD · Founder'
              : `${adminProfile?.institutionName ?? adminProfile?.institutionType ?? 'Institution'} · Admin`;
            return (
              <>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', background: '#bf1e2e',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 12, color: '#fff', flexShrink: 0,
                }}>{initials}</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#f0f4ff', lineHeight: 1.2 }}>{name}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-3)' }}>{sublabel}</div>
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </aside>
  );
}
